import type { UMRange } from '../_components';
import { CRM_FIELD_IDS, FIELDS, fullLabel } from './catalog';
import type { BuildingAttrs, DateValue, DeclaredValue, FieldDef, FilterValue, FilterValues } from './types';

export const TODAY = new Date();
const DAY = 86_400_000;
export const isoDay = (d: Date) => d.toISOString().slice(0, 10);
export const addDays = (days: number) => isoDay(new Date(TODAY.getTime() + days * DAY));

export const normalize = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

export function isActive(f: FieldDef, v: FilterValue): boolean {
  if (v === undefined) return false;
  switch (f.kind) {
    case 'radio':
      return v !== f.options![0];
    case 'segmented':
    case 'select':
      return false;
    case 'toggle':
      return v === true;
    case 'distance': {
      const [a, b] = v as [number, number];
      return a > 0 || b < f.max!;
    }
    case 'range': {
      const r = v as UMRange;
      return Boolean(r.min || r.max);
    }
    case 'dateRange': {
      const d = v as DateValue;
      return Boolean(d.preset) && (d.preset !== 'Personnalisée' || Boolean(d.from || d.to));
    }
    case 'declared': {
      const d = v as DeclaredValue;
      return d.state !== 'Indifférent' || Boolean(d.min || d.max);
    }
    default:
      return Array.isArray(v) ? v.length > 0 : Boolean(v);
  }
}

/** « Présent dans une offre » as applied: any other CRM criterion implies « Oui » (pre-design CRM rule). */
export function effectiveCrmPresent(values: FilterValues): string {
  const stored = (values.crmPresent as string) ?? 'Indifférent';
  if (stored !== 'Indifférent') return stored;
  return CRM_FIELD_IDS.some((id) => isActive(FIELDS[id], values[id])) ? 'Oui' : stored;
}

export function dateBounds(d: DateValue): [string, string] {
  switch (d.preset) {
    case '< 6 mois': return [isoDay(TODAY), addDays(182)];
    case '< 1 an': return [isoDay(TODAY), addDays(365)];
    case '< 2 ans': return [isoDay(TODAY), addDays(730)];
    case '3 derniers mois': return [addDays(-91), isoDay(TODAY)];
    case '12 derniers mois': return [addDays(-365), isoDay(TODAY)];
    case 'À venir': return [isoDay(TODAY), '9999-12-31'];
    default: return [d.from || '0000-01-01', d.to || '9999-12-31'];
  }
}

const inRange = (x: number | null, r: { min: string; max: string }) =>
  x !== null && (r.min === '' || x >= Number(r.min)) && (r.max === '' || x <= Number(r.max));

function fieldMatches(f: FieldDef, v: FilterValue, attr: BuildingAttrs[string]): boolean {
  switch (f.kind) {
    case 'range':
      return inRange(attr as number | null, v as UMRange);
    case 'radio':
      return v === 'Oui' ? attr === true : attr === false;
    case 'multiselect':
    case 'checkboxes':
    case 'dpe': {
      const sel = v as string[];
      if (attr === null) return false;
      return Array.isArray(attr) ? attr.some((a) => sel.includes(a)) : sel.includes(String(attr));
    }
    case 'text':
    case 'perimeter': {
      const q = normalize(v as string);
      if (attr === null) return false;
      return (Array.isArray(attr) ? attr : [String(attr)]).some((a) => normalize(a).includes(q));
    }
    case 'exact':
      return attr !== null && normalize(String(attr)) === normalize((v as string).trim());
    case 'dateRange': {
      if (attr === null) return false;
      const [from, to] = dateBounds(v as DateValue);
      return (attr as string) >= from && (attr as string) <= to;
    }
    case 'declared': {
      const d = v as DeclaredValue;
      if (d.state === 'Non') return attr === null;
      if (attr === null) return false;
      return inRange(attr as number, d);
    }
    case 'distance': {
      const [a, b] = v as [number, number];
      const x = attr as number;
      return x >= a && (b >= f.max! - f.step! || x <= b);
    }
    default:
      return true;
  }
}

export function buildingMatches(attrs: BuildingAttrs, values: FilterValues): boolean {
  const crm = effectiveCrmPresent(values);
  if (crm === 'Oui' && attrs.crmPresent !== true) return false;
  if (crm === 'Non' && attrs.crmPresent !== false) return false;

  for (const [id, v] of Object.entries(values)) {
    const f = FIELDS[id];
    if (!f || id === 'crmPresent' || !isActive(f, v)) continue;
    if (crm === 'Non' && CRM_FIELD_IDS.includes(id)) continue;
    if (id === 'usages') {
      const sel = v as string[];
      const primary = sel.includes(attrs.usages as string);
      const secondary = values.secondaryPurpose === true && attrs.secondaryUsage !== null && sel.includes(attrs.secondaryUsage as string);
      if (!primary && !secondary) return false;
      continue;
    }
    if (id === 'secondaryPurpose') continue;
    if (!fieldMatches(f, v, attrs[id])) return false;
  }
  return true;
}

const fmtDate = (iso: string) => (iso ? iso.split('-').reverse().join('/') : '…');
const list = (xs: string[]) => (xs.length > 2 ? `${xs.slice(0, 2).join(', ')} +${xs.length - 2}` : xs.join(', '));
const range = (r: { min: string; max: string }) => (r.min && r.max ? `${r.min}–${r.max}` : r.min ? `≥ ${r.min}` : `≤ ${r.max}`);

/** Text of a summary chip, e.g. « Nombre de niveaux : 3–10 ». */
export function chipText(f: FieldDef, v: FilterValue, values: FilterValues): string {
  const label = fullLabel(f);
  switch (f.kind) {
    case 'range': return `${label} : ${range(v as UMRange)}`;
    case 'radio': return `${label} : ${v as string}`;
    case 'toggle': return label;
    case 'text': return `${label} : « ${v as string} »`;
    case 'exact': return `${label} : ${v as string}`;
    case 'perimeter': return `Périmètre : ${v as string}`;
    case 'distance': {
      const [a, b] = v as [number, number];
      return `Distance : ${a}–${b >= f.max! - f.step! ? `${b}+` : b} m`;
    }
    case 'dateRange': {
      const d = v as DateValue;
      return `${label} : ${d.preset === 'Personnalisée' ? `${fmtDate(d.from)} → ${fmtDate(d.to)}` : d.preset}`;
    }
    case 'declared': {
      const d = v as DeclaredValue;
      const parts = [d.state !== 'Indifférent' ? (d.state === 'Oui' ? 'renseigné' : 'non renseigné') : '', d.min || d.max ? range(d) : ''].filter(Boolean);
      return `${label.replace(' (MWh)', '')} ${(values.declaredYear as string) ?? FIELDS.declaredYear.options![0]} : ${parts.join(', ')}`;
    }
    default: return `${label} : ${list(v as string[])}`;
  }
}

/** Value a field goes back to when its chip is removed or the panel is reset. */
export function emptyValue(f: FieldDef): FilterValue {
  switch (f.kind) {
    case 'radio': return f.options![0];
    case 'range': return { min: '', max: '' };
    case 'distance': return [0, f.max!];
    case 'dateRange': return { preset: '', from: '', to: '' };
    case 'declared': return { state: 'Indifférent', min: '', max: '' };
    case 'toggle': return false;
    case 'multiselect':
    case 'checkboxes':
    case 'dpe': return [];
    default: return '';
  }
}
